import type { IndustryPage } from "@/lib/data";

export const buildingFirms: IndustryPage = {
  slug: "building-firms",
  seoTitle:
    "Building Company Website Design UK | £1,000, Live in 7 Days",
  seoDescription:
    "Building company website design: completed schemes, sector pages, capacity and the accreditations you hold. Fixed £1,000, live in 7 days. Book a call.",
  serviceName: "Website design for building firms",
  tldr:
    "Building company website design from ConstructionWebDesign.uk gives UK building firms a site that shows completed schemes with sector and value band, live projects, team and capacity, and the accreditations you hold, such as CHAS or Constructionline. The price is £1,000, one-off and fixed, with no subscription. Your website goes live in 7 days, and you own it.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Building company website design · UK",
    headline: "Website design for building firms that shows what you can deliver.",
    highlight: "what you can deliver",
    sub: "Websites for UK building firms with a page per completed scheme showing sector and value band, live projects, sector pages, a team and capacity page, the accreditations you hold and a tender-style enquiry form.",
    secondaryCta: { label: "See building firm demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/hero/extension_builder_slider_ratio.jpg",
      alt: "Building firm's site team working on a residential scheme in progress",
      headlineTop: "Win better projects,",
      headlineBottom: "not just more of them.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/coreframe.jpg",
    },
  ],
  demoSlugs: ["coreframe", "stackline", "constructum", "northcrest", "halden"],
  demosHeading: "Building company website demo templates.",
  demosSub:
    "Demo templates for building firms, not client work. Open one to see how a completed-scheme list, sector pages and a capability section read to a developer or architect.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does a building company need a website?",
    intro:
      "A building company needs a website because developers, architects and repeat commercial clients look you up before deciding whether to send a tender. The website is where they check what you have finished, how big it was and whether you hold the accreditations their checklist asks for.",
    points: [
      {
        title: "Show the size of work you can carry.",
        body: "A firm delivering large residential schemes and a firm fitting kitchens can look the same on a generic template. Value bands, programme lengths and sector labels on every completed project show a developer or architect the scale of work you take on, at a glance.",
        icon: "Buildings",
      },
      {
        title: "Give every completed scheme its own page.",
        body: "Each completed scheme gets a page with client type, sector, contract value band, programme, photography and what you did when the job got difficult. A developer or architect can read the projects closest to their own before deciding whether to invite you to tender.",
        icon: "Sparkle",
      },
      {
        title: "Answer the capacity question early.",
        body: "Buyers want to know how much work you can carry at once: directly employed trades, plant, live sites and typical contract size. Setting it out plainly on a team and capacity page means a capable firm is judged on facts rather than on how big it looks.",
        icon: "Crane",
      },
      {
        title: "Make pre-qualification checks easier.",
        body: "Constructionline, CHAS, SafeContractor, ISO 9001, employers' liability and professional indemnity cover, and CSCS cards for your workforce sit on one page with numbers and renewal dates. A buyer can work through their checklist without emailing you for it, and the page only lists what you actually hold.",
        icon: "Shield",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What does building company website design include?",
    intro:
      "Building company website design from ConstructionWebDesign.uk includes a page per completed scheme, live projects, sector pages, a team and capacity page, an accreditation and compliance page, a PQQ-ready page and a tender-style enquiry form. Each one answers a question a developer, architect or contracts manager asks before inviting you to price.",
    items: [
      {
        title: "A page per completed scheme.",
        body: "Each scheme page sets out the sector, client type, contract value band, programme, photography and the problem you solved. Because every scheme has its own page, the site covers the kind of work you want more of, not just your company name.",
      },
      {
        title: "Live projects and recent starts.",
        body: "A current-work section shows what is on site now, with the sector and location of each job. It shows buyers the firm is busy and working, and gives you something to update every month without rewriting the site.",
      },
      {
        title: "Sector pages.",
        body: "Residential developments, commercial fit-out, education, healthcare, industrial, refurbishment, and listed and heritage work each get their own page, written for the buyer in that sector rather than the same paragraph reworded.",
      },
      {
        title: "Team and capacity page.",
        body: "Directors, contracts managers, site managers, directly employed trades, plant and typical concurrent workload. The page answers the capacity question before it turns into an objection you never hear.",
      },
      {
        title: "Accreditation and compliance page.",
        body: "CHAS (Contractors Health and Safety Assessment Scheme), SafeContractor, Constructionline, ISO 9001 and ISO 14001, insurance cover levels and your health and safety policy sit in one place, with registration numbers linked to the official register. Only the schemes you hold are shown.",
      },
      {
        title: "A PQQ-ready page you can send as a link.",
        body: "Common pre-qualification questionnaire (PQQ) questions, such as accreditations, insurance cover, health and safety policy, similar project examples and turnover band, can be answered on one page. When a PQQ or tender lands, you send the link instead of rebuilding the same pack.",
      },
      {
        title: "A tender-style enquiry form.",
        body: "The form captures sector, location, contract value band, programme dates and whether drawings exist, then goes to the person who prices the work instead of a shared inbox nobody owns.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a building company website cost in the UK?",
      a: "A building company website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription. The Website + Brand package costs £1,399 and adds a logo, visual identity, brand style direction and business card and document mockups. Typical UK ranges are about £4,000–£5,000 for a freelancer, and £5,000–£25,000 and 8–12 weeks for an agency. You own the finished website.",
    },
    {
      q: "How long does it take to build a building company website?",
      a: "A building company website from ConstructionWebDesign.uk goes live in 7 days from the discovery call. The five steps are a discovery call, strategy and sitemap, design, build, then launch and support. The week runs to plan when project photography, scheme details, accreditation numbers and insurance levels are ready at the discovery call.",
    },
    {
      q: "What should a building company website include?",
      a: "A building company website should include a page for each completed scheme with sector and contract value band, a live projects section, sector pages, a team and capacity page, an accreditation page listing what you hold, such as CHAS, SafeContractor or Constructionline, your insurance cover levels and an enquiry form built for tenders. Each section answers a question buyers ask at shortlist stage.",
    },
    {
      q: "Does a building firm need a website?",
      a: "Yes, a building firm needs a website, because developers, architects and commercial clients typically look a contractor up online before adding them to a tender list. A building firm without a website gives buyers no easy way to see completed schemes, capacity or accreditations, so the firm is judged on a name and a phone number alone.",
    },
    {
      q: "Do construction company websites need SEO?",
      a: "Yes, construction company websites need SEO so buyers searching for a contractor by sector and region can find them. Every building company website from ConstructionWebDesign.uk includes on-page local SEO: page titles, headings and copy that name your sectors and the regions you work in, plus a separate page for each completed scheme and sector. No one can honestly guarantee rankings, but that structure gives Google clear information about the work you do.",
    },
    {
      q: "What is the difference between a building firm website and a builder's website?",
      a: "A building firm website is aimed at developers, architects and repeat commercial clients who shortlist from evidence, while a domestic builder's website is aimed at homeowners comparing quotes. A building firm website from ConstructionWebDesign.uk leads with contract value bands, sectors, capacity and pre-qualification details rather than before and after photos of extensions.",
    },
    {
      q: "Can a building company website help with PQQs and tenders?",
      a: "Yes, a building company website can make pre-qualification questionnaires (PQQs) and tenders quicker to complete by putting the common answers on one page you can link to. The accreditations you hold, such as CHAS, SafeContractor or Constructionline, plus insurance cover levels, your health and safety policy, similar project examples and turnover band can all sit on that page, so you don't rebuild the same pack for every tender.",
    },
    {
      q: "Can a building firm website show projects delivered as a subcontractor?",
      a: "Yes, a building company website can show projects delivered as a subcontractor, as long as the contract role is labelled clearly. ConstructionWebDesign.uk labels the role on every project page, such as principal contractor, subcontractor or design and build, so the record is accurate. Buyers check references, and an overstated role costs more credibility than a smaller, accurate one.",
    },
  ],
  guides: [
    {
      href: "/blog/how-much-should-a-construction-website-cost",
      label: "How much does a construction website cost in the UK?",
    },
    {
      href: "/blog/how-to-create-a-website-for-a-construction-company",
      label: "How to create a website for a construction company",
    },
    {
      href: "/blog/chas-accreditation-explained",
      label: "CHAS accreditation explained: cost, process, worth it?",
    },
    {
      href: "/blog/how-to-win-subcontractor-work",
      label: "How to win subcontractor work and get on tender lists",
    },
  ],
  finalCta: {
    headline: "Show buyers what your firm has delivered.",
    highlight: "what your firm has delivered",
    sub: "Book a free 30-minute call to talk through your schemes, sectors and capacity. Fixed £1,000 price, and your website goes live in 7 days.",
  },
};
